import { Component, OnInit, Input, OnChanges } from '@angular/core';
import { Router } from '@angular/router';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { NgbDateParserFormatter, NgbCalendar } from '@ng-bootstrap/ng-bootstrap';
import { NgbDateFRParserFormatter } from './ngb-date-fr-parser-formatter';
import { CommonService } from '../../services/common/common.service';
import { ReportService } from '../../services/report/report.service';
import { REPORT_DATE_RANGES, downloadCsv } from '../../report-view';

interface Slice { label: string; value: number; color: string; }
interface TypeRow { label: string; scheduled: number; performed: number; cancelled: number; note?: string; }
interface CountyRow { county: string; scheduled: number; performed: number; cancelled: number; }

/** The per-property lists from the old report, in its order (Property Details section). */
const DETAIL_LISTS = [
  { key: 'original_and_limited', label: 'Scheduled · Original and Limited' },
  { key: 'on_hold', label: 'Scheduled · On Hold' },
  { key: 'reinspection', label: 'Scheduled · Reinspection' },
  { key: 'supplemental', label: 'Scheduled · Supplemental' },
  { key: 'all_sheduled', label: 'Scheduled · All' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'performed_orginal', label: 'Performed · Original' },
  { key: 'limited_charged', label: 'Performed · Limited (Charged)' },
  { key: 'limited_no_charge', label: 'Performed · Limited (No Charge)' },
  { key: 'performed_reinspection', label: 'Performed · Reinspection' },
  { key: 'performed_supplemental', label: 'Performed · Supplemental' },
];

/**
 * Inspections Scheduled (mockup reports/inspections-scheduled). Same API (inspection_report),
 * filters, date presets and inspector rule. Metric tiles, the two pie charts, an inspection type
 * breakdown and a county breakdown (both with cancellations, from the API), plus the old
 * per-property lists and On Hold count kept under Property Details. Also embedded on the old
 * admin dashboard / home widgets ([count]), where the page head is hidden as before.
 */
@Component({
  selector: 'app-inspections-scheduled',
  templateUrl: './inspections-scheduled.component.html',
  styleUrls: ['./inspections-scheduled.component.scss'],
  providers: [{ provide: NgbDateParserFormatter, useClass: NgbDateFRParserFormatter }]
})
export class InspectionsScheduledComponent implements OnInit, OnChanges {
  @Input() count: number;
  readonly embedded: boolean;
  readonly dateRanges = REPORT_DATE_RANGES;
  readonly detailLists = DETAIL_LISTS;

  report = this.emptyFilters();
  branches = [];
  inspectors = [];
  /** Branch and Inspector filters: not for inspectors (the API limits them to their own jobs). */
  inspectorCheck = false;
  loaded = false;
  errordetails = false;
  loading = false;
  today = new Date();

  head: any = {};
  tableData: any = {};
  scheduledSlices: Slice[] = [];
  performedSlices: Slice[] = [];
  scheduledPie: SafeStyle;
  performedPie: SafeStyle;
  typeRows: TypeRow[] = [];
  countyRows: CountyRow[] = [];
  open = { types: true, counties: false, details: false };
  openList: { [key: string]: boolean } = {};

  constructor(private _router: Router, private calendar: NgbCalendar, private commonService: CommonService,
    private reportService: ReportService, private sanitizer: DomSanitizer) {
    this.embedded = _router.url.split('?')[0] !== '/accounting-report-inspections-scheduled';
  }

  ngOnInit() {
    this.commonService.getBranch().subscribe(data => this.branches = data);
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    this.inspectorCheck = !!user && user.user_level != 'inspector';
    if (this.inspectorCheck) {
      this.commonService.getInspectors().subscribe(data => this.inspectors = data);
    }
  }

  ngOnChanges() {
    // Date range picked on the dashboard widget.
    if (this.count) {
      this.report.dateRange = this.count;
      this.dateRangeSubmit(this.count);
      this.inspectionReport();
    }
  }

  private emptyFilters() {
    return { branch: '', inspector: '', start: null, end: null, dateRange: 0 as any };
  }

  inspectionReport() {
    if (!this.report.start || !this.report.end) {
      this.errordetails = true;
      return false;
    }
    this.errordetails = false;
    this.loading = true;
    this.reportService.getInspections(this.report).subscribe(
      data => {
        this.loading = false;
        this.loaded = true;
        this.today = new Date();
        const results = (data && data.data && data.data.results) || {};
        this.head = results.head_data || {};
        this.tableData = results.table_data || {};
        this.build(results.county_data || {});
      },
      () => this.loading = false
    );
  }

  private n(value: any): number {
    const v = +value;
    return isFinite(v) ? v : 0;
  }

  private build(county: any) {
    const s = this.head.scheduled || {};
    const p = this.head.performed || {};
    const c = this.head.cancelled_by_type || {};

    this.scheduledSlices = [
      { label: 'Original & Limited', value: this.n(s.original_and_limited), color: '#2f71b6' },
      { label: 'On Hold', value: this.n(s.on_hold), color: '#8e6bc4' },
      { label: 'Reinspection', value: this.n(s.reinspection), color: '#74b9ec' },
      { label: 'Supplemental', value: this.n(s.supplemental), color: '#e7b940' },
      { label: 'Intake Incomplete', value: this.n(s.intake_incomplete), color: '#a9b8c9' },
      { label: 'Cancelled', value: this.n(s.cancelled), color: '#da5a61' },
    ];
    this.performedSlices = [
      { label: 'Original', value: this.n(p.original), color: '#2f71b6' },
      { label: 'Limited · Charged', value: this.n(p.limited_charged), color: '#74b9ec' },
      { label: 'Limited · No Charge', value: this.n(p.limited_no_harge), color: '#a9b8c9' },
      { label: 'Reinspection', value: this.n(p.reinspection), color: '#e7b940' },
      { label: 'Supplemental', value: this.n(p.supplemental), color: '#51b657' },
    ];

    this.typeRows = [
      { label: 'Original & Limited', scheduled: this.n(s.original_and_limited),
        performed: this.n(p.original) + this.n(p.limited_charged) + this.n(p.limited_no_harge), cancelled: this.n(c.original_and_limited) },
      { label: 'On Hold', note: 'Original & Limited on hold', scheduled: this.n(s.on_hold), performed: null, cancelled: null },
      { label: 'Reinspection', scheduled: this.n(s.reinspection), performed: this.n(p.reinspection), cancelled: this.n(c.reinspection) },
      { label: 'Supplemental', scheduled: this.n(s.supplemental), performed: this.n(p.supplemental), cancelled: this.n(c.supplemental) },
      { label: 'Intake Incomplete', note: 'No inspection date yet', scheduled: this.n(s.intake_incomplete), performed: null, cancelled: null },
    ];
    if (this.n(c.other)) {
      this.typeRows.push({ label: 'Other', scheduled: null, performed: null, cancelled: this.n(c.other) });
    }

    const names: { [k: string]: boolean } = {};
    ['original_and_limited', 'performed_orginal_limited', 'cancelled_original_limited']
      .forEach(k => Object.keys(county[k] || {}).forEach(name => names[name] = true));
    this.countyRows = Object.keys(names).sort().map(name => ({
      county: name,
      scheduled: this.n((county.original_and_limited || {})[name]),
      performed: this.n((county.performed_orginal_limited || {})[name]),
      cancelled: this.n((county.cancelled_original_limited || {})[name]),
    }));
    this.openList = {};
    this.scheduledPie = this.pie(this.scheduledSlices);
    this.performedPie = this.pie(this.performedSlices);
  }

  clear() {
    this.report = this.emptyFilters();
    this.errordetails = false;
  }

  dateRangeSubmit(value) {
    this.report.end = this.calendar.getToday();
    this.report.start = this.commonService.dateRangeSubmit(value);
  }

  // ---- Charts (CSS conic-gradient pies, as in the mockup) ----

  sliceTotal(slices: Slice[]): number {
    return slices.reduce((t, s) => t + s.value, 0);
  }

  // conic-gradient isn't on Angular 6's safe-style list; the stops are our own numbers and colours.
  pie(slices: Slice[]): SafeStyle {
    const total = this.sliceTotal(slices);
    let css = '#e6ebf2';
    if (total) {
      let at = 0;
      const stops = slices.filter(s => s.value > 0).map(s => {
        const from = at;
        at += s.value / total * 100;
        return s.color + ' ' + from.toFixed(3) + '% ' + at.toFixed(3) + '%';
      });
      css = 'conic-gradient(' + stops.join(', ') + ')';
    }
    return this.sanitizer.bypassSecurityTrustStyle(css);
  }

  pieLabel(slices: Slice[]): string {
    return slices.map(s => s.value + ' ' + s.label).join(', ');
  }

  // ---- Breakdowns ----

  rate(row: TypeRow): string {
    if (row.performed === null || !row.scheduled) {
      return '—';
    }
    return (row.performed / row.scheduled * 100).toFixed(2) + '%';
  }

  get typeTotals(): TypeRow {
    const s = this.head.scheduled || {};
    return {
      label: 'Total',
      scheduled: this.n(s.total),
      performed: this.n((this.head.performed || {}).total),
      cancelled: this.n(s.cancelled),
    };
  }

  countyTotal(field: string): number {
    return this.countyRows.reduce((t, r) => t + r[field], 0);
  }

  list(key: string): any[] {
    const value = this.tableData[key];
    return value ? (Array.isArray(value) ? value : Object.keys(value).map(k => value[k])) : [];
  }

  get detailCount(): number {
    return DETAIL_LISTS.reduce((t, d) => t + this.list(d.key).length, 0);
  }

  get period(): string {
    const s = this.head.scheduled || {};
    return s.datim_start ? s.datim_start + ' – ' + s.datim_end : '';
  }

  // Every per-property row, with the breakdowns' figures above them.
  exportCsv() {
    const rows: any[][] = [['Inspection Type', 'Scheduled', 'Performed', 'Cancelled', 'Completion Rate']];
    this.typeRows.concat([this.typeTotals]).forEach(r => rows.push([r.label, r.scheduled, r.performed, r.cancelled, this.rate(r)]));
    rows.push([]);
    rows.push(['County (Original & Limited)', 'Scheduled', 'Performed', 'Cancelled']);
    this.countyRows.forEach(r => rows.push([r.county, r.scheduled, r.performed, r.cancelled]));
    rows.push([]);
    rows.push(['List', 'Property', 'Date', 'Report #', 'Notes']);
    DETAIL_LISTS.forEach(d => this.list(d.key).forEach(r => rows.push([d.label, r.prop_address, r.sched_date, r.job_id, r.status_note || ''])));
    downloadCsv('inspections-scheduled.csv', rows);
  }
}
